import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { dashboardColors, theme } from "./../../themes/Theme";
import { KonfhubButton } from "../Atom/Buttons";

const DEFAULT_LOGO = "/favicon/android-chrome-512x512.png";

const ShareQRCode = ({
    url,
    logo = DEFAULT_LOGO,
    size = 300, // max size
    color = dashboardColors[theme].primary,
    background = "#ffffff",
    margin = 2,
    borderRadius = 0,
    className = "",
    style = {},
    ...qrProps
}) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [renderSize, setRenderSize] = useState(size);

    // 🔹 Adjust QR size based on parent container width
    useEffect(() => {
        const resize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const newSize = Math.min(size, width * 0.8); // cap at parent width * 80%
                setRenderSize(Math.max(120, newSize)); // min 120px
            }
        };

        resize();
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, [size]);

    // 🔹 Generate QR code
    useEffect(() => {
        if (!canvasRef.current || !url) return;
        let isMounted = true;

        QRCode.toCanvas(
            canvasRef.current,
            url,
            {
                width: renderSize,
                margin,
                color: { dark: color, light: background },
                ...qrProps,
            },
            (err) => {
                if (err || !isMounted || !canvasRef.current) return;

                if (logo) {
                    const ctx = canvasRef.current.getContext("2d");
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.src = logo;
                    img.onload = () => {
                        if (!isMounted || !canvasRef.current) return;
                        const logoSize = renderSize * 0.24;
                        const x = (canvasRef.current.width - logoSize) / 2;
                        const y = (canvasRef.current.height - logoSize) / 2;

                        ctx.fillStyle = "#fff";
                        ctx.beginPath();
                        ctx.moveTo(x + borderRadius, y);
                        ctx.lineTo(x + logoSize - borderRadius, y);
                        ctx.quadraticCurveTo(x + logoSize, y, x + logoSize, y + borderRadius);
                        ctx.lineTo(x + logoSize, y + logoSize - borderRadius);
                        ctx.quadraticCurveTo(
                            x + logoSize,
                            y + logoSize,
                            x + logoSize - borderRadius,
                            y + logoSize
                        );
                        ctx.lineTo(x + borderRadius, y + logoSize);
                        ctx.quadraticCurveTo(
                            x,
                            y + logoSize,
                            x,
                            y + logoSize - borderRadius
                        );
                        ctx.lineTo(x, y + borderRadius);
                        ctx.quadraticCurveTo(x, y, x + borderRadius, y);
                        ctx.closePath();
                        ctx.fill();

                        ctx.drawImage(img, x, y, logoSize, logoSize);
                    };
                }
            }
        );

        return () => {
            isMounted = false;
        };
    }, [url, logo, renderSize, color, background, margin, borderRadius, qrProps]);

    const handleDownload = () => {
        if (!canvasRef.current) return;
        const link = document.createElement("a");
        link.download = "qrcode.png";
        link.href = canvasRef.current.toDataURL("image/png");
        link.click();
    };

    if (!url) return null;

    return (
        <div
            ref={containerRef}
            className={`text-center my-4 ${className}`}
            style={{
                borderRadius: 4,
                width: "100%",
                maxWidth: "400px",
                margin: "0 auto",
                ...style,
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    width: "100%",
                    height: "auto",
                    maxWidth: `${renderSize}px`,
                }}
                aria-label="QR code"
            />
            <KonfhubButton
                btnType="secondary"
                onClick={handleDownload}
                style={{
                    display: "initial"
                }}
            >
                Download
            </KonfhubButton>
        </div>
    );
};

export default ShareQRCode;
